import { Box } from '@mentimeter/ragnar-ui/box';
import { scaleBand, scaleLinear } from 'd3-scale';
import { stack } from 'd3-shape';
import { Fragment, useCallback, useMemo } from 'react';
import type {
  Accessor,
  ChoicesStaticOptions,
  SegmentedChoicesData,
  Visualization,
} from '../../types';
import { getElement } from '../../utils/non-empty-array';
import { SvgWithFont } from '../../utils/SvgWithFont';
import type {
  RenderChoiceLabel,
  RenderChoiceVisualization,
  RenderChoiceVisualizationItem,
} from '../../utils/types/render-options-by-slide-type/types';
import { BarsAxisLabels } from '../BarsAxisLabels';
import {
  LABELS_HEIGHT,
  LOW_OPACITY,
  MAX_BAR_WIDTH,
  MIN_BAR_HEIGHT,
  MIN_MARGIN_TOP,
  VALUE_TEXT_HEIGHT,
  VALUE_TEXT_MARGIN,
} from '../constants';
import { Score, type CorrectnessIndicatorStatus } from '../Score';
import { calculateBarSpacing } from '../utils/calculateBarSpacing';
import { HiddenScoreBars } from '../hidden-score-bars/HiddenScoreBars';
import { EmptyBars } from './EmptyBars';
import { getSegmentData, type SegmentData } from './getSegmentData';
import { SegmentRectangle } from './SegmentRectangle';
import { SegmentsLegend } from './SegmentsLegend';
import { useSegmentHover } from './useSegmentHover';
import {
  getBarPercentageGetter,
  getBarTotalById,
  getMinMaxIndexes,
  getSegmentPercentage,
} from './utils';

const BAR_LABELS_HEIGHT = 100;

interface Segment {
  id: number | null;
  label: string;
  position: number;
}

const getSegmentOpacity = ({
  isSegmentHovered,
  isAnySegmentHovered,
  correctnessIndicatorStatus,
}: {
  isSegmentHovered: boolean;
  isAnySegmentHovered: boolean;
  correctnessIndicatorStatus: CorrectnessIndicatorStatus;
}) => {
  if (isAnySegmentHovered && !isSegmentHovered) {
    return LOW_OPACITY;
  }
  if (
    correctnessIndicatorStatus.isVisible &&
    !correctnessIndicatorStatus.isCorrect
  ) {
    return LOW_OPACITY;
  }
  return 1;
};

interface SegmentedBarsProps
  extends Visualization<SegmentedChoicesData, ChoicesStaticOptions> {
  spacingPercentage?: number;
  segments: Segment[];
  segmentKeyAccessor?: (d: Segment) => number;
  sourceQuestionTitle: string;
  shouldShowScorePercentage: false | { percentOf: number };
  shouldShowCorrect: boolean;
  correctAnswerAccessor?: Accessor<ChoicesStaticOptions, boolean>;
  placeHolderColor: string;
  isScoreHidden: boolean;
  renderChoiceVisualization?: RenderChoiceVisualization | undefined;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
}

export const SegmentedBars = ({
  colors,
  textColor,
  data,
  staticOptions,
  sourceQuestionTitle,
  keyAccessor = (d: SegmentedChoicesData | ChoicesStaticOptions) =>
    String(d.id),
  labelAccessor = (d: ChoicesStaticOptions) => d.label,
  spacingPercentage,
  width: rootWidth,
  height: rootHeight,
  segments,
  segmentKeyAccessor = (s: Segment) => s.position,
  shouldShowScorePercentage,
  disableAnimations = false,
  shouldShowCorrect,
  correctAnswerAccessor = (d) => d.isMarkedCorrect,
  placeHolderColor,
  isScoreHidden,
  fontFamily,
  renderChoiceVisualization,
  renderChoiceLabel,
}: SegmentedBarsProps) => {
  const { onSegmentHover, getIsSegmentHovered, isAnySegmentHovered } =
    useSegmentHover();

  const legendWidth = rootWidth * 0.3;
  const svgWidth = rootWidth - legendWidth;

  const calculatedSpacing =
    spacingPercentage ??
    calculateBarSpacing({
      containerWidth: svgWidth,
      numBars: staticOptions.length,
    });

  const maxBoundedWidth =
    (MAX_BAR_WIDTH * staticOptions.length) / (1 - calculatedSpacing);
  const boundedWidth = Math.min(svgWidth, maxBoundedWidth);
  const boundsLeftMargin = (svgWidth - boundedWidth) / 2;

  const discreteValues = staticOptions.map(keyAccessor);
  const discreteScale = scaleBand()
    .domain(discreteValues)
    .range([0, boundedWidth])
    .paddingInner(calculatedSpacing)
    .paddingOuter(0.00001); // having this at 0 makes the first bar disappear, I have no idea what I'm doing wrong

  const svgHeight = Math.max(rootHeight - BAR_LABELS_HEIGHT, 0);
  const boundedHeight = Math.max(svgHeight - MIN_MARGIN_TOP, 0);

  const stackSeries = stack().keys(
    segments.map(segmentKeyAccessor).map(String),
  );
  const stackData = getSegmentData(data, segments, segmentKeyAccessor);
  const series = stackSeries(stackData);

  const yMax = Math.max(
    ...series.map((segmentArray) =>
      Math.max(...segmentArray.map((dd) => dd[1])),
    ),
  );

  const yScale = useMemo(() => {
    if (yMax === 0) {
      /**
       * Linear scale doesn't work well with domain [0, 0].
       *
       * @see https://stackoverflow.com/questions/70577576/d3-js-scalelinear-is-not-returning-the-correct-output-for-0-value
       */
      return () => 0;
    }

    return scaleLinear().domain([0, yMax]).range([0, boundedHeight]);
  }, [yMax, boundedHeight]);

  const getRectanglePosition = useCallback(
    (d: SegmentedChoicesData) => {
      const key = keyAccessor(d);
      const value = d.id;
      const x = discreteScale(key);
      const y = boundedHeight - yScale(value);
      const width = discreteScale.bandwidth();
      const height = yScale(value);
      const step = discreteScale.step();
      const gap = step - width;

      return { x, y, width, height, gap };
    },
    [discreteScale, keyAccessor, boundedHeight, yScale],
  );

  const visualizationChoices = useMemo(() => {
    if (typeof renderChoiceVisualization !== 'function') {
      return undefined;
    }

    const choices: Array<RenderChoiceVisualizationItem> = data.map((d) => {
      const id = keyAccessor(d);
      const position = getRectanglePosition(d);
      return { id, position };
    });

    return choices;
  }, [renderChoiceVisualization, data, keyAccessor, getRectanglePosition]);

  const getCorrectnessIndicatorStatus = useCallback(
    (barId: number): CorrectnessIndicatorStatus => {
      const staticOption = staticOptions.find(
        (option) => keyAccessor(option) === String(barId),
      );
      const isCorrect = Boolean(
        staticOption && correctAnswerAccessor(staticOption),
      );
      return {
        isVisible: shouldShowCorrect,
        isCorrect,
      };
    },
    [correctAnswerAccessor, keyAccessor, shouldShowCorrect, staticOptions],
  );

  const targetMinMaxIndexes = getMinMaxIndexes(data);

  const visualization = (
    <SvgWithFont
      width={svgWidth}
      height={svgHeight}
      fontFamily={fontFamily}
      aria-hidden
    >
      <g
        data-testid="bounds"
        width={boundedWidth}
        height={boundedHeight}
        transform={`translate(${boundsLeftMargin}, ${MIN_MARGIN_TOP})`}
      >
        {isScoreHidden ? (
          <HiddenScoreBars
            data={data.map((d) => ({
              id: d.id,
              score: d.scores.reduce((acc, score) => acc + score, 0),
            }))}
            staticOptions={staticOptions}
            keyAccessor={keyAccessor}
            xScale={discreteScale}
            colors={colors}
            disableAnimations={disableAnimations}
            boundedHeight={boundedHeight}
            images={null}
          />
        ) : (
          <g data-testid="segmented-bars">
            <EmptyBars
              data={data}
              discreteScale={discreteScale}
              boundedHeight={boundedHeight}
              textColor={textColor}
              placeHolderColor={placeHolderColor}
              shouldShowPercent={shouldShowScorePercentage}
              disableAnimations={disableAnimations}
              getCorrectnessIndicatorStatus={getCorrectnessIndicatorStatus}
            />
            {series.map((segmentArray, i) => {
              return (
                <g key={`segmented-bar-${i}`} data-name={segmentArray.key}>
                  {segmentArray.map((group, j) => {
                    const barId = (group.data as SegmentData).id;
                    const x = discreteScale(String(barId)) || 0;
                    const rectangleY = boundedHeight - yScale(group[1]);

                    const color = getElement(colors, Number(segmentArray.key));

                    const isLowestRectangleInBar =
                      targetMinMaxIndexes[j]?.minIndex === i;
                    const isHighestRectangleInBar =
                      targetMinMaxIndexes[j]?.maxIndex === i;

                    const value = group[1] - group[0];
                    const height = yScale(value);

                    const correctnessIndicatorStatus =
                      getCorrectnessIndicatorStatus(barId);

                    return (
                      <Fragment key={`segment-fragment-${i}-${j}`}>
                        {value > 0 && (
                          <SegmentRectangle
                            value={
                              shouldShowScorePercentage
                                ? value.toString()
                                : getSegmentPercentage(value, barId, data)
                            }
                            x={x}
                            y={rectangleY}
                            height={height}
                            width={discreteScale.bandwidth()}
                            color={color}
                            isBottomRectangleInBar={isLowestRectangleInBar}
                            isTopRectangleInBar={isHighestRectangleInBar}
                            animateFromY={boundedHeight - height}
                            opacity={getSegmentOpacity({
                              isSegmentHovered: getIsSegmentHovered(
                                Number(segmentArray.key),
                              ),
                              isAnySegmentHovered,
                              correctnessIndicatorStatus,
                            })}
                            disableAnimations={disableAnimations}
                            onMouseEnter={() =>
                              onSegmentHover(Number(segmentArray.key))
                            }
                            onMouseLeave={() => onSegmentHover(null)}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </g>
              );
            })}
          </g>
        )}
      </g>
    </SvgWithFont>
  );

  const visualizationOverride = renderChoiceVisualization?.({
    node: visualization,
    boundedWidth,
    boundsLeftMargin,
    choices: visualizationChoices ?? [],
  });

  if (svgHeight <= 0 || svgWidth <= 0) {
    return null;
  }

  const getBarScoreDisplayText = (barId: number) => {
    if (shouldShowScorePercentage) {
      const getBarPercentage = getBarPercentageGetter(
        data,
        shouldShowScorePercentage.percentOf,
      );
      return getBarPercentage(barId);
    } else {
      return getBarTotalById(barId, data).toString();
    }
  };

  return (
    <Box flexDirection="row" alignItems="center" height={rootHeight}>
      <Box flexDirection="column" width={svgWidth} height="100%">
        {visualizationOverride ?? visualization}
        <Box
          data-testid="scores"
          width={boundedWidth}
          height={boundedHeight}
          top={MIN_MARGIN_TOP}
          left={boundsLeftMargin}
          position="absolute"
          // to enable SVG behind to be clickable
          style={{ pointerEvents: 'none' }}
        >
          {data.map((d) => {
            const barId = d.id;
            const barTotalScore = d.scores.reduce(
              (acc, score) => acc + score,
              0,
            );
            const yScaleValue =
              barTotalScore === 0 ? MIN_BAR_HEIGHT : yScale(barTotalScore);
            const rectangleY = boundedHeight - yScaleValue;
            const x = discreteScale(String(barId)) || 0;
            return (
              <Score
                key={`score-${barId}`}
                x={x}
                y={rectangleY - VALUE_TEXT_MARGIN}
                barWidth={discreteScale.bandwidth()}
                textColor={textColor}
                height={VALUE_TEXT_HEIGHT}
                scoreDisplayText={
                  isScoreHidden ? '' : getBarScoreDisplayText(barId)
                }
                correctnessIndicatorStatus={getCorrectnessIndicatorStatus(
                  barId,
                )}
                disableAnimations={disableAnimations}
              />
            );
          })}
        </Box>
        <BarsAxisLabels
          staticOptions={staticOptions}
          labelAccessor={labelAccessor}
          keyAccessor={keyAccessor}
          discreteScale={discreteScale}
          totalWidth={boundedWidth}
          totalHeight={LABELS_HEIGHT}
          marginLeft={boundsLeftMargin}
          marginTop={20}
          color={textColor}
          fontFamily={fontFamily}
          renderChoiceLabel={renderChoiceLabel}
        />
      </Box>
      <SegmentsLegend
        maxHeight={rootHeight}
        sourceQuestionTitle={sourceQuestionTitle}
        segments={segments.map((segment) => ({
          ...segment,
          color: getElement(colors, segmentKeyAccessor(segment)),
          opacity: getSegmentOpacity({
            isSegmentHovered: getIsSegmentHovered(segmentKeyAccessor(segment)),
            isAnySegmentHovered,
            correctnessIndicatorStatus: {
              isVisible: false, // correct answers don't relate to segments
              isCorrect: false,
            },
          }),
        }))}
        segmentKeyAccessor={segmentKeyAccessor}
        onSegmentHover={onSegmentHover}
      />
    </Box>
  );
};
